/**
 * Página admin: completude de dados dos freelancers.
 *
 * Mostra o quão preenchido está o cadastro (áreas, avaliações, portfólio,
 * LinkedIn) e deixa claro quais alavancas rendem mais recomendação boa. Permite
 * disparar o processamento de portfólios (análise de IA) com progresso ao vivo,
 * controle de pause/resume/parar e custo de IA (acumulado + previsão).
 *
 * Auth: ProtectedRoute requiredScreen="completude_dados" + JWT no backend.
 */
import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sparkles, Image as ImageIcon, AlertTriangle, Loader2, Pause, Play, Ban } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  getCoverage,
  getLatestRun,
  startBatch,
  controlBatch,
  type CoverageSinal,
  type FunilEtapa,
  type AreaDemanda,
  type BatchAction,
  type DemandaPerdida,
  type LadoReprovacao,
  type MotivoReprovacao,
} from "@/services/portfolio-coverage-api";
import { formatCentsBRL } from "@/utils/money";
import { toUserError } from "@/lib/api-error";

/** Motivos de reprovação (enum do banco) em linguagem de negócio, agrupados por quem reprovou. */
const MOTIVO_REPROVACAO_LABELS: Record<string, string> = {
  corisco_falta_perfis_adequados: "Faltou perfil adequado",
  corisco_falta_disponibilidade_perfis: "Perfis sem disponibilidade",
  corisco_indicacoes_fora_prazo: "Indicações fora do prazo",
  cliente_perfis_nao_aprovados: "Não aprovou os perfis",
  cliente_sem_resposta_perfis: "Não respondeu aos perfis",
  cliente_motivos_alheios_indicacoes: "Motivos alheios às indicações",
  sem_motivo: "Sem motivo registrado",
};

const LADO_REPROVACAO_LABELS: Record<LadoReprovacao, string> = {
  corisco: "Nós",
  cliente: "Cliente",
  sem_motivo: "Sem motivo",
};

/** Ordem de leitura: primeiro o que é nosso, depois o cliente, por fim o que falta registrar. */
const LADO_ORDER: LadoReprovacao[] = ["corisco", "cliente", "sem_motivo"];

/** Motivos de skip em linguagem de negócio. */
const SKIP_LABELS: Record<string, string> = {
  linkedin_login_wall: "Só tem LinkedIn (pede login)",
  instagram_login_wall: "Só tem Instagram (pede login)",
  sem_url: "Sem link de portfólio",
  nao_e_url: "Link inválido",
  link_quebrado: "Link quebrado (não abre)",
  arquivo_nao_renderizavel: "Arquivo (Drive/PDF) não renderizável",
  rede_social_nao_portfolio: "Rede social (não é portfólio)",
};

/** Custo de IA vem em USD da OpenAI (não é centavo/BRL). */
const usd = (v: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 4 }).format(v);

function pctColor(pct: number): string {
  if (pct >= 70) return "text-emerald-600";
  if (pct >= 30) return "text-amber-600";
  return "text-rose-600";
}

/** Cor do share de alocação por área (concentração): quanto maior a fatia, mais forte. */
function shareColor(pct: number): string {
  if (pct >= 20) return "text-emerald-600";
  if (pct >= 5) return "text-amber-600";
  return "text-muted-foreground";
}

/** Uma etapa do funil de uso: barra proporcional à % da base + conversão da etapa anterior. */
function FunnelRow({ stage, isFirst }: { stage: FunilEtapa; isFirst: boolean }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="text-foreground">{stage.label}</span>
        <span className="tabular-nums font-medium">
          {stage.count.toLocaleString("pt-BR")}{" "}
          <span className="text-muted-foreground font-normal">· {stage.pct_base}% da base</span>
        </span>
      </div>
      <Progress value={stage.pct_base} className="h-2" />
      {!isFirst && stage.pct_prev != null && (
        <div className="text-[11px] text-muted-foreground">
          {stage.pct_prev}% da etapa anterior
        </div>
      )}
    </div>
  );
}

/** Linha da tabela de oferta x demanda. Marca em âmbar áreas sem demanda registrada. */
function AreaRow({ row, totalAllocations }: { row: AreaDemanda; totalAllocations: number }) {
  const hasNoDemand = row.vagas === 0 && row.alocacoes === 0;
  const formatNumber = (value: number) => value.toLocaleString("pt-BR");
  const allocationShare = totalAllocations > 0 ? Math.round((100 * row.alocacoes) / totalAllocations) : 0;
  return (
    <TableRow>
      <TableCell className="py-2">
        <span className="inline-flex items-center gap-1.5">
          {hasNoDemand && (
            <span
              className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500"
              title="Sem vaga nem alocação registrada nessa área"
            />
          )}
          {row.area}
        </span>
      </TableCell>
      <TableCell className="py-2 text-right tabular-nums">{formatNumber(row.freelancers)}</TableCell>
      <TableCell className={`py-2 text-right tabular-nums ${row.vagas === 0 ? "text-muted-foreground" : ""}`}>
        {formatNumber(row.vagas)}
      </TableCell>
      <TableCell className={`py-2 text-right tabular-nums ${row.alocacoes === 0 ? "text-muted-foreground" : ""}`}>
        {formatNumber(row.alocacoes)}
        {row.alocacoes > 0 && allocationShare > 0 && (
          <span className={`ml-1.5 text-[11px] font-normal ${shareColor(allocationShare)}`}>{allocationShare}%</span>
        )}
      </TableCell>
    </TableRow>
  );
}

function sortMotivos(motivos: MotivoReprovacao[]): MotivoReprovacao[] {
  return [...motivos].sort(
    (a, b) => LADO_ORDER.indexOf(a.quem) - LADO_ORDER.indexOf(b.quem) || b.vagas - a.vagas,
  );
}

/** Linha de vagas perdidas; o lado "nós" fica em destaque porque é a única perda que a base explica. */
function MotivoRow({ row }: { row: MotivoReprovacao }) {
  const isOurs = row.quem === "corisco";
  return (
    <TableRow className={isOurs ? "bg-rose-50/40 dark:bg-rose-950/20" : undefined}>
      <TableCell className={`py-2 ${isOurs ? "font-medium" : "text-muted-foreground"}`}>
        {LADO_REPROVACAO_LABELS[row.quem]}
      </TableCell>
      <TableCell className="py-2">{MOTIVO_REPROVACAO_LABELS[row.motivo] ?? row.motivo}</TableCell>
      <TableCell className="py-2 text-right tabular-nums">{row.vagas.toLocaleString("pt-BR")}</TableCell>
      <TableCell className="py-2 text-right tabular-nums">{formatCentsBRL(row.verba_maxima_cents)}</TableCell>
    </TableRow>
  );
}

/** Resumo em uma frase: quantas perdemos por motivo nosso e quanto de verba isso representa. */
function DemandaPerdidaResumo({ data }: { data: DemandaPerdida }) {
  const nossas = data.motivos.filter((row) => row.quem === "corisco");
  const nossasVagas = nossas.reduce((sum, row) => sum + row.vagas, 0);
  const nossaVerba = nossas.reduce((sum, row) => sum + row.verba_maxima_cents, 0);
  const pctComMotivo = data.reprovadas > 0 ? Math.round((100 * data.com_motivo) / data.reprovadas) : 0;
  const pctNossas = data.reprovadas > 0 ? Math.round((100 * nossasVagas) / data.reprovadas) : 0;
  return (
    <p className="text-sm text-muted-foreground">
      Nos últimos {data.janela_meses} meses: {data.total_vagas.toLocaleString("pt-BR")} vagas,{" "}
      {data.aprovadas.toLocaleString("pt-BR")} aprovadas e {data.reprovadas.toLocaleString("pt-BR")} reprovadas
      ({pctComMotivo}% com motivo registrado). Perdidas por motivo nosso:{" "}
      <span className="font-medium text-foreground">
        {nossasVagas.toLocaleString("pt-BR")} vagas ({pctNossas}% das reprovadas), {formatCentsBRL(nossaVerba)} de verba máxima
      </span>
      .
    </p>
  );
}

function CoverageRow({ signal }: { signal: CoverageSinal }) {
  const missing = Math.max(0, signal.total - signal.filled);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="text-foreground">{signal.label}</span>
        <span className={`tabular-nums font-medium ${pctColor(signal.pct)}`}>
          {signal.pct}%{" "}
          <span className="text-muted-foreground font-normal">({signal.filled}/{signal.total})</span>
        </span>
      </div>
      <Progress value={signal.pct} className="h-1.5" />
      {missing > 0 && (
        <div className="text-[11px] text-muted-foreground">
          faltam <span className="font-medium text-foreground/80 tabular-nums">{missing.toLocaleString("pt-BR")}</span> perfis
        </div>
      )}
    </div>
  );
}

export default function CompletudeDados() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: coverage, isLoading: loadingCov } = useQuery({
    queryKey: ["portfolio-coverage"],
    queryFn: getCoverage,
  });

  // Poll do progresso a cada 3s enquanto há run em andamento.
  const { data: latest } = useQuery({
    queryKey: ["portfolio-batch-latest"],
    queryFn: getLatestRun,
    refetchInterval: (query) => (query.state.data?.run?.status === "running" ? 3000 : false),
  });

  const run = latest?.run ?? null;
  const projection = latest?.cost_projection ?? null;

  // Run "running" sem heartbeat há >5min é tratada como morta (o backend já
  // marca como erro no próximo poll; aqui é cinto+suspensório pra não travar a UI).
  const isStale =
    run?.status === "running" && Date.now() - Date.parse(run.updated_at.replace(" ", "T")) > 5 * 60_000;
  const isRunning = run?.status === "running" && !isStale;
  const isPaused = run?.status === "paused";
  const canResume = run != null && (run.status === "paused" || run.status === "error");

  const invalidateLatest = () => queryClient.invalidateQueries({ queryKey: ["portfolio-batch-latest"] });

  const startMutation = useMutation({
    mutationFn: () => startBatch(),
    onSuccess: (res) => {
      if (res.already_running) {
        toast({ title: "Já tem um processamento rodando", description: "Acompanhe o progresso abaixo." });
      } else {
        toast({ title: "Processamento iniciado", description: "Os portfólios estão sendo analisados." });
      }
      invalidateLatest();
    },
    onError: (error: unknown) => {
      toast({ title: "Erro ao iniciar", description: toUserError(error).description, variant: "destructive" });
    },
  });

  const controlMutation = useMutation({
    mutationFn: ({ runId, action }: { runId: string; action: BatchAction }) => controlBatch(runId, action),
    onSuccess: (_res, vars) => {
      const labels: Record<BatchAction, string> = {
        pause: "Processamento pausado",
        resume: "Processamento retomado",
        cancel: "Processamento parado",
      };
      toast({ title: labels[vars.action] });
      invalidateLatest();
    },
    onError: (error: unknown) => {
      toast({ title: "Erro no controle", description: toUserError(error).description, variant: "destructive" });
    },
  });

  const control = (action: BatchAction) => {
    if (!run) return;
    if (action === "cancel" && !window.confirm("Parar o processamento? Os pendentes ficam para depois.")) return;
    controlMutation.mutate({ runId: run.id, action });
  };

  // Quando uma run termina, atualiza as métricas de cobertura.
  const runStatus = run?.status;
  useEffect(() => {
    if (runStatus === "done") {
      queryClient.invalidateQueries({ queryKey: ["portfolio-coverage"] });
    }
  }, [runStatus, queryClient]);

  const portfolio = coverage?.portfolio;
  const totalAllocations = coverage?.areas.reduce((sum, row) => sum + row.alocacoes, 0) ?? 0;
  const pending = latest?.pending ?? portfolio?.pending ?? 0;
  const progressPct = run && run.target > 0 ? Math.round((run.processed / run.target) * 100) : 0;
  const costPerItem = run && run.paid_count > 0 ? run.cost_usd / run.paid_count : null;
  const busy = startMutation.isPending || controlMutation.isPending;

  // Rótulo do box de progresso por status.
  const statusLabel =
    run?.status === "running" ? (isStale ? "Execução travou" : "Processando...") :
    run?.status === "paused" ? "Pausado" :
    run?.status === "cancelled" ? "Parado" :
    run?.status === "error" ? "Erro na execução" :
    "Último lote concluído";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Completude de dados"
        subtitle="Quanto mais informação cada freelancer tem, melhor a recomendação."
      />

      {/* Cobertura por sinal */}
      <section className="space-y-3">
        <div>
          <h2 className="text-base font-semibold">O que está preenchido na base</h2>
          <p className="text-sm text-muted-foreground">
            {coverage ? `${coverage.total_freelancers} freelancers ativos` : "Carregando..."}
          </p>
        </div>
        {loadingCov && Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} className="h-8 w-full" />)}
        {coverage?.sinais.map((signal) => <CoverageRow key={signal.key} signal={signal} />)}
      </section>

      {/* Funil de uso da base: quanto da base ativa vira visualização, seleção e alocação */}
      <section className="space-y-3">
        <div>
          <h2 className="text-base font-semibold">Uso da base</h2>
          <p className="text-sm text-muted-foreground">
            Quanto da base ativa é efetivamente vista, selecionada e alocada.
          </p>
        </div>
        {loadingCov && Array.from({ length: 5 }).map((_, index) => <Skeleton key={index} className="h-8 w-full" />)}
        {coverage?.funil.map((stage, index) => <FunnelRow key={stage.key} stage={stage} isFirst={index === 0} />)}
        {coverage && (
          <p className="pt-1 text-[11px] text-muted-foreground">
            "Alocados" = freelancers ativos postos em projeto no mesmo período dos sinais.
            {coverage.alocacao_footprint && (
              <>
                {" "}No total, desde {coverage.alocacao_footprint.desde_ano}, já passaram{" "}
                {coverage.alocacao_footprint.ativos} ativos por{" "}
                {coverage.alocacao_footprint.projetos.toLocaleString("pt-BR")} projetos.
              </>
            )}{" "}
            "Shortlist" inclui quem foi aprovado; visualizações só desde mai/2026.
          </p>
        )}
      </section>

      {/* Oferta x demanda por área: dimensiona a "base útil" */}
      <section className="space-y-3">
        <div>
          <h2 className="text-base font-semibold">Oferta × demanda por área</h2>
          <p className="text-sm text-muted-foreground">
            Onde tem freelancer, onde tem vaga e onde a gente mais aloca. Áreas com
            <span className="mx-1 inline-block h-1.5 w-1.5 rounded-full bg-amber-500 align-middle" />
            não têm vaga nem alocação registrada.
          </p>
        </div>
        {loadingCov && Array.from({ length: 8 }).map((_, index) => <Skeleton key={index} className="h-7 w-full" />)}
        {coverage && (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Área</TableHead>
                  <TableHead className="text-right">Freelancers</TableHead>
                  <TableHead className="text-right">Vagas</TableHead>
                  <TableHead className="text-right">Alocações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {coverage.areas.map((row) => (
                  <AreaRow key={row.area} row={row} totalAllocations={totalAllocations} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {coverage && (
          <p className="pt-1 text-[11px] text-muted-foreground">
            Alocações = freelancers distintos alocados na área (não conta o mesmo frila 2×); o %
            é a fatia da área no total. Leitura direcional: os nomes de área diferem entre cadastro
            de freelancer, vagas e projetos, então uma área com "0 vagas" pode ter demanda com outro nome.
          </p>
        )}
      </section>

      {/* Vagas perdidas por motivo: quanto da demanda escapa e por culpa de quem */}
      <section className="space-y-3">
        <div>
          <h2 className="text-base font-semibold">Vagas perdidas por motivo</h2>
          {coverage?.demanda_perdida ? (
            <DemandaPerdidaResumo data={coverage.demanda_perdida} />
          ) : (
            <p className="text-sm text-muted-foreground">
              Quantas vagas reprovamos, por quem e por quê, e a verba que passou junto.
            </p>
          )}
        </div>
        {loadingCov && Array.from({ length: 5 }).map((_, index) => <Skeleton key={index} className="h-7 w-full" />)}
        {coverage?.demanda_perdida && (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quem reprovou</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead className="text-right">Vagas</TableHead>
                  <TableHead className="text-right">Verba máxima</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortMotivos(coverage.demanda_perdida.motivos).map((row) => (
                  <MotivoRow key={`${row.quem}-${row.motivo}`} row={row} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {coverage?.demanda_perdida && (
          <p className="pt-1 text-[11px] text-muted-foreground">
            Verba máxima = soma do teto de verba das vagas reprovadas naquele motivo, o que passou pela porta
            e não virou projeto. Só a linha "Nós" fala de base ou operação; o lado do cliente serve de
            comparação. "Sem motivo" precisa de preenchimento na vaga, não de análise.
          </p>
        )}
      </section>

      {/* Portfólio: situação + disparo do batch */}
      <section className="space-y-4">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <ImageIcon className="h-4 w-4" />
            Análise de portfólios
          </h2>
          <p className="text-sm text-muted-foreground">
            A IA analisa o portfólio (criatividade, estética, clientes notáveis) e isso entra no
            ranking.
          </p>
        </div>
        {portfolio && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Analisados" value={portfolio.analyzed} tone="emerald" />
              <Stat label="A processar" value={pending} tone="indigo" />
              <Stat label="Pulados" value={portfolio.skipped_total} tone="slate" />
              <Stat label="Falharam" value={portfolio.failed} tone="rose" />
            </div>
          )}

          {/* Botões de controle */}
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={() => startMutation.mutate()}
              disabled={isRunning || isPaused || busy || pending === 0}
              className="gap-1.5"
            >
              {isRunning || startMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              Processar todos os pendentes
            </Button>

            {isRunning && (
              <Button variant="outline" className="gap-1.5" disabled={busy} onClick={() => control("pause")}>
                <Pause className="h-4 w-4" /> Pausar
              </Button>
            )}
            {canResume && pending > 0 && (
              <Button variant="outline" className="gap-1.5" disabled={busy} onClick={() => control("resume")}>
                <Play className="h-4 w-4" /> Retomar
              </Button>
            )}
            {(isRunning || isPaused) && (
              <Button variant="ghost" className="gap-1.5 text-rose-600 hover:text-rose-700" disabled={busy} onClick={() => control("cancel")}>
                <Ban className="h-4 w-4" /> Parar
              </Button>
            )}

            <span className="text-sm text-muted-foreground">
              {pending ? `${pending.toLocaleString("pt-BR")} aguardando análise` : "Nada pendente"}
              {pending ? " · processa todos automaticamente" : ""}
            </span>
          </div>

          {run && (
            <div className="space-y-2 rounded-md border border-border/50 bg-muted/40 p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{statusLabel}</span>
                <span className="tabular-nums text-muted-foreground">
                  {run.processed}/{run.target} · {run.ok_count} ok · {run.fail_count} falhas
                </span>
              </div>
              <Progress value={progressPct} className="h-2" />

              {/* Custo de IA (USD) */}
              <div className="flex flex-wrap gap-x-6 gap-y-1 pt-1 text-xs text-muted-foreground">
                <span>
                  custo de IA (USD):{" "}
                  <span className="font-medium tabular-nums text-foreground/80">{usd(run.cost_usd)}</span>
                </span>
                <span>
                  por portfólio:{" "}
                  <span className="font-medium tabular-nums text-foreground/80">
                    {costPerItem != null ? usd(costPerItem) : "-"}
                  </span>
                </span>
                <span>
                  previsão final:{" "}
                  <span className="font-medium tabular-nums text-foreground/80">
                    {projection != null ? usd(projection) : "-"}
                  </span>
                </span>
              </div>
            </div>
          )}

          {run?.status === "error" && (
            <p className="flex items-center gap-1.5 text-sm text-rose-600">
              <AlertTriangle className="h-4 w-4" /> Erro na última execução: {run.error}
            </p>
          )}

          {/* Motivos de skip (curadoria) */}
          {portfolio && Object.keys(portfolio.skipped_by_reason).length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">
                Portfólios pulados (precisam de correção no cadastro):
              </p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(portfolio.skipped_by_reason).map(([reason, count]) => (
                  <Badge key={reason} variant="secondary" className="font-normal">
                    {SKIP_LABELS[reason] ?? reason}: {count}
                  </Badge>
                ))}
              </div>
            </div>
          )}
      </section>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: string }) {
  const toneMap: Record<string, string> = {
    emerald: "text-emerald-600",
    indigo: "text-indigo-600",
    slate: "text-slate-600",
    rose: "text-rose-600",
  };
  return (
    <div className="rounded-md border border-border/50 p-3 text-center">
      <div className={`text-2xl font-semibold tabular-nums ${toneMap[tone] ?? ""}`}>{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}
